/**
 * Phase L / UI polish — ThemeToggle (three-icon segmented control
 * with self-explanation labels).
 *
 * PRESENTATION-ONLY polish on top of the segmented-control refactor
 * (`7d98cb7`). Adds:
 *   - Full-size variant (default, rendered in the 3 shells): each
 *     segment renders `<Icon><span>Label</span>` (Light / Auto / Dark).
 *   - Floating variant (rendered on the 6 form surfaces via
 *     `FloatingThemeToggle`): icons-only + native `title` tooltip per
 *     segment for hover disambiguation.
 *   - "Auto" segment specifically: full-size shows `title="Match my
 *     device setting"` so hovering the short "Auto" label reveals
 *     the plain-language explanation.
 *
 * NO theme logic, storage, or precedence changes:
 *   - `pooplucker_theme_mode` localStorage key unchanged.
 *   - Tri-state values ("auto" | "light" | "dark") unchanged.
 *   - ThemeProvider precedence (explicit > OS-pref > time-fallback)
 *     unchanged.
 *   - All 9 render sites unchanged.
 *
 * Accessibility (WAI-ARIA radiogroup) unchanged from `7d98cb7`:
 *   - Container: `role="radiogroup" aria-label="Theme preference"`.
 *   - Each segment: `role="radio" aria-checked + aria-label`.
 *     Missed-Items Combined Batch (Item 1) polish — aria-label copy
 *     was tightened to the user-locked verbatim triple:
 *       Light : "Use light theme"
 *       Auto  : "Match my device setting"  (user-locked)
 *       Dark  : "Use dark theme"
 *     `title` mirrors `aria-label` on every segment in BOTH variants
 *     so hover disambiguation is consistent across labeled + compact.
 *   - Roving tabindex: only checked segment has `tabIndex=0`.
 *   - ArrowLeft/Right/Up/Down move selection (wrap around).
 *   - Home/End jump to first/last.
 *   - Space/Enter fall through to native click.
 *   - Focus-visible outline via `--pp-orange`.
 *
 * Testid stability:
 *   - `data-testid="theme-toggle-btn"` PRESERVED on container.
 *   - Per-segment testids PRESERVED: `theme-segment-{light|auto|dark}`.
 */
import React, { useRef } from "react";
import { Sun, Monitor, Moon } from "lucide-react";
import { useTheme } from "@/theme/ThemeProvider";

/**
 * Segment table.
 *   - `mode`: storage value ("light" | "auto" | "dark").
 *   - `Icon`: lucide component.
 *   - `label`: short visible text for the full-size variant.
 *   - `ariaLabel`: user-locked verbatim aria-label (Missed-Items
 *      Combined Batch — Item 1).
 *   - `title`: native tooltip. Mirrors `ariaLabel` verbatim in BOTH
 *      variants — labeled + compact — so hover disambiguation is
 *      consistent whether or not the text label is visible.
 */
const SEGMENTS = [
  {
    mode: "light",
    Icon: Sun,
    label: "Light",
    ariaLabel: "Use light theme",
    title: "Use light theme",
    testid: "theme-segment-light",
  },
  {
    mode: "auto",
    Icon: Monitor,
    label: "Auto",
    ariaLabel: "Match my device setting",
    title: "Match my device setting",
    testid: "theme-segment-auto",
  },
  {
    mode: "dark",
    Icon: Moon,
    label: "Dark",
    ariaLabel: "Use dark theme",
    title: "Use dark theme",
    testid: "theme-segment-dark",
  },
];

export default function ThemeToggle({ size = 32, variant = "labeled" }) {
  const { mode, effective, setMode } = useTheme();
  const segRefs = useRef([]);
  const currentIdx = Math.max(
    0,
    SEGMENTS.findIndex((s) => s.mode === mode),
  );

  const compact = variant === "compact";

  const _selectAndFocus = (idx) => {
    const next = SEGMENTS[idx];
    if (!next) return;
    setMode(next.mode);
    requestAnimationFrame(() => {
      const el = segRefs.current[idx];
      if (el && typeof el.focus === "function") el.focus();
    });
  };

  const _onKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      _selectAndFocus((currentIdx + 1) % SEGMENTS.length);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      _selectAndFocus((currentIdx - 1 + SEGMENTS.length) % SEGMENTS.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      _selectAndFocus(0);
    } else if (e.key === "End") {
      e.preventDefault();
      _selectAndFocus(SEGMENTS.length - 1);
    }
  };

  // Sizing:
  //   Compact (floating) = square chips (icon-only), same shape as `7d98cb7`.
  //   Labeled (full-size) = icon + text; horizontal padding grows the chip;
  //     height mirrors the compact square-chip so the pill height matches
  //     the topbar layout expectations across shells.
  const iconPx = Math.round(size * 0.5); // 16px @ default 32
  const chipHeight = size;
  const chipPadX = compact ? 0 : 12;
  const chipPadY = 0;
  const chipMinW = compact ? size : "auto";
  const pillPad = 3;

  return (
    <div
      role="radiogroup"
      aria-label="Theme preference"
      data-testid="theme-toggle-btn"
      data-theme-mode={mode}
      data-theme-effective={effective}
      data-variant={compact ? "compact" : "labeled"}
      onKeyDown={_onKeyDown}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: pillPad,
        gap: 2,
        borderRadius: 999,
        background: "var(--pp-surface-2)",
        border: "1px solid var(--pp-border)",
      }}
    >
      {SEGMENTS.map((seg, idx) => {
        const checked = mode === seg.mode;
        const { Icon } = seg;
        return (
          <button
            key={seg.mode}
            ref={(el) => (segRefs.current[idx] = el)}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={seg.ariaLabel}
            data-testid={seg.testid}
            data-theme-segment={seg.mode}
            data-checked={checked ? "true" : "false"}
            tabIndex={checked ? 0 : -1}
            // Missed-Items Combined Batch (Item 1) — `title` mirrors
            // `aria-label` verbatim on every segment in BOTH variants.
            title={seg.title}
            onClick={() => {
              if (!checked) setMode(seg.mode);
            }}
            style={{
              minWidth: chipMinW,
              height: chipHeight,
              padding: `${chipPadY}px ${chipPadX}px`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: compact ? 0 : 6,
              borderRadius: 999,
              background: checked ? "var(--pp-surface)" : "transparent",
              color: checked ? "var(--pp-orange)" : "var(--pp-text-muted)",
              boxShadow: checked ? "var(--pp-card-shadow)" : "none",
              border: "1px solid transparent",
              transition:
                "background 150ms ease, color 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
              cursor: checked ? "default" : "pointer",
              outline: "none",
              // Text sizing consistent with the codebase's --pp-text-body
              // font stack (inherit for family; size explicit).
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              if (!checked) e.currentTarget.style.color = "var(--pp-orange-hover)";
            }}
            onMouseLeave={(e) => {
              if (!checked) e.currentTarget.style.color = "var(--pp-text-muted)";
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "var(--pp-orange)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "transparent";
            }}
          >
            <Icon size={iconPx} aria-hidden="true" />
            {!compact && (
              <span data-testid={`theme-segment-${seg.mode}-label`}>
                {seg.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
